/**
 * HorizontalSeriesRow Component
 *
 * Netflix-style horizontal scrolling row for featured series.
 */

import type { ReactElement } from 'react';
import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Library, BookOpen, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export interface HorizontalSeries {
  id: string;
  title: string;
  description?: string;
  cover_image_url?: string;
  thumbnail?: string;
  age_range: string;
  book_count?: number;
  author_name?: string;
  author_picture?: string;
  book_summaries?: Array<{ thumbnail?: string }>;
}

interface HorizontalSeriesRowProps {
  title: string;
  series: HorizontalSeries[];
  loading?: boolean;
  onSeeAll?: () => void;
  onSeriesClick?: (seriesId: string) => void;
}

// Helper to get full image URL
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const HorizontalSeriesRow = ({
  title,
  series,
  loading = false,
  onSeeAll,
  onSeriesClick,
}: HorizontalSeriesRowProps): ReactElement | null => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScrollPosition = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, [series]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 400;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleSeriesClick = (seriesId: string) => {
    if (onSeriesClick) {
      onSeriesClick(seriesId);
    } else {
      navigate(`/series/public/${seriesId}`);
    }
  };

  // Don't render if no series and not loading
  if (!loading && series.length === 0) {
    return null;
  }

  return (
    <div className="relative group/row">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3 px-1">
        <h3
          className="font-bold text-base flex items-center gap-2"
          style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
        >
          <Library className="w-5 h-5" style={{ color: '#6366F1' }} />
          {title}
        </h3>
        {onSeeAll && (
          <button
            onClick={onSeeAll}
            className="text-xs font-semibold hover:underline flex items-center gap-1"
            style={{ fontFamily: 'Quicksand, sans-serif', color: '#6366F1' }}
          >
            See all
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Scroll Container */}
      <div className="relative">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-all duration-200 hover:scale-110"
            style={{
              background: 'var(--st-bg-surface)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid var(--st-border-light)',
            }}
          >
            <ChevronLeft className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
          </button>
        )}

        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-all duration-200 hover:scale-110"
            style={{
              background: 'var(--st-bg-surface)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid var(--st-border-light)',
            }}
          >
            <ChevronRight className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
          </button>
        )}

        {/* Series Row */}
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto pb-2 horizontal-scroll-row"
          onScroll={checkScrollPosition}
        >
          {loading ? (
            // Loading Skeletons
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-[200px] h-[180px] rounded-xl animate-pulse"
                style={{ background: 'var(--st-border-light)' }}
              />
            ))
          ) : (
            series.map((s) => (
              <div
                key={s.id}
                onClick={() => handleSeriesClick(s.id)}
                className="flex-shrink-0 w-[200px] rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.03] hover:shadow-lg"
                style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
              >
                {/* Cover */}
                <div
                  className="h-[100px] relative overflow-hidden"
                  style={{ background: 'var(--st-bg-muted)' }}
                >
                  {s.cover_image_url || s.book_summaries?.[0]?.thumbnail ? (
                    <img
                      src={getImageUrl(s.cover_image_url || s.book_summaries?.[0]?.thumbnail) || undefined}
                      alt={s.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Library className="w-10 h-10" style={{ color: 'var(--st-text-muted)' }} />
                    </div>
                  )}

                  {/* Book Count Badge */}
                  <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 bg-white/95 dark:bg-gray-800/95" style={{ color: 'var(--st-text-heading)' }}>
                    <BookOpen className="w-3 h-3" />
                    {s.book_count || 0} {s.book_count === 1 ? 'book' : 'books'}
                  </div>
                </div>

                {/* Content */}
                <div className="p-2.5">
                  {/* Title */}
                  <h4
                    className="font-bold text-sm line-clamp-1 mb-1"
                    style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                  >
                    {s.title}
                  </h4>

                  {/* Description */}
                  {s.description && (
                    <p
                      className="text-[10px] line-clamp-1 mb-1.5"
                      style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                    >
                      {s.description}
                    </p>
                  )}

                  {/* Author */}
                  <div className="flex items-center gap-1.5">
                    {s.author_picture ? (
                      <img
                        src={getImageUrl(s.author_picture) || undefined}
                        alt={s.author_name}
                        className="w-4 h-4 rounded-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ background: 'var(--st-bg-muted)' }}
                      >
                        <User className="w-2.5 h-2.5" style={{ color: 'var(--st-text-muted)' }} />
                      </div>
                    )}
                    <span
                      className="text-[10px] font-medium truncate"
                      style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                    >
                      {s.author_name || 'Anonymous'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HorizontalSeriesRow;
